import { cn } from "@bednbody/ui/lib/utils";
import type { Icon } from "@phosphor-icons/react";
import {
  CalendarDotsIcon,
  ChartBarIcon,
  MoneyIcon,
  PaperPlaneRightIcon,
  PlusIcon,
  UsersIcon,
  WalletIcon,
  XIcon,
} from "@phosphor-icons/react";
import { useState } from "react";
import { Link } from "react-router-dom";
import type { StaffRole } from "@/lib/jwt-claims";
import { canAccess } from "@/lib/permissions";

interface QuickAction {
  label: string;
  href: string;
  icon: Icon;
}

/**
 * Shortcuts to the surfaces where the actual mutations live — every ops write
 * goes through its own module (and Edge Function), so the dashboard links
 * rather than duplicating those flows. Visibility follows route access
 * (permissions.ts is the matrix App.tsx routes from).
 */
const ACTIONS: QuickAction[] = [
  { label: "Dispatch a rider", href: "/dispatch", icon: PaperPlaneRightIcon },
  { label: "Bookings", href: "/bookings", icon: CalendarDotsIcon },
  { label: "Roster", href: "/roster", icon: UsersIcon },
  { label: "Cash remittance", href: "/cash", icon: MoneyIcon },
  { label: "Run payroll", href: "/payroll", icon: WalletIcon },
  { label: "Analytics", href: "/analytics", icon: ChartBarIcon },
];

export function QuickActionsBar({ role }: { role: StaffRole }) {
  const [open, setOpen] = useState(false);
  const actions = ACTIONS.filter((a) => canAccess(role, a.href));
  if (actions.length === 0) return null;

  return (
    <div className="fixed bottom-6 right-6 z-40 flex flex-col items-end gap-2">
      {open && (
        <ul className="flex flex-col items-end gap-1.5">
          {actions.map((a) => {
            const Icon = a.icon;
            return (
              <li key={a.href}>
                <Link
                  to={a.href}
                  onClick={() => setOpen(false)}
                  className="flex items-center gap-2 rounded-full border border-border bg-card py-2 pl-3.5 pr-4 text-sm font-medium text-foreground shadow-md transition-transform hover:-translate-y-0.5 hover:text-primary"
                >
                  <Icon size={15} className="text-primary" />
                  {a.label}
                </Link>
              </li>
            );
          })}
        </ul>
      )}
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-label={open ? "Close quick actions" : "Open quick actions"}
        className={cn(
          "flex size-12 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-lg transition-transform hover:scale-105",
          open && "rotate-90",
        )}
      >
        {open ? <XIcon size={20} /> : <PlusIcon size={20} />}
      </button>
    </div>
  );
}
